import React, { createContext, Dispatch, ReactNode, SetStateAction, useContext, useMemo, useState } from 'react';
import useWorkspacesEffects from '../../hooks/context/effects/useWorkspacesEffects';
import useWorkspacesContext from '../../hooks/context/useWorkspacesContext';
import { Workspace, WorkspaceScore } from '../../types/files';
import { AuthContext } from '../AuthContext';
import useWorkspaces from '../../hooks/useWorkspaces';
import storage from '../../utils/storage';

type WorkspacesContextProps = {
  workspacesLoaded: boolean;
  workspaces: Workspace[];
  setWorkspaces: Dispatch<SetStateAction<Workspace[]>>;
  workspacesCollection: Record<string, Workspace[]>;
  setWorkspacesCollection: React.Dispatch<React.SetStateAction<Record<string, Workspace[]>>>;
  acceptedWorkspaces: (Workspace & { group?: string;})[];
  pendingWorkspaces: (Workspace & { group?: string;})[];
  workspaceScores: WorkspaceScore[];
  setWorkspaceScores: Dispatch<SetStateAction<WorkspaceScore[]>>;
};

export const WorkspacesContext = createContext<WorkspacesContextProps>({
  workspacesLoaded: false,
  workspaces: [],
  setWorkspaces: () => {},
  workspacesCollection: {},
  setWorkspacesCollection: () => {},
  acceptedWorkspaces: [],
  pendingWorkspaces: [],
  workspaceScores: [],
  setWorkspaceScores: () => {},
});

const WorkspacesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { userGroup } = useContext(AuthContext);
  const {
    workspaces,
    workspaceScores,
    setWorkspaces,
    setWorkspaceScores
  } = useWorkspacesContext();
  const { acceptedWorkspaces, pendingWorkspaces } = useWorkspaces();
  const { workspacesLoaded } = useWorkspacesEffects(userGroup);

  const [workspacesCollection, setWorkspacesCollection] = useState<Record<string, Workspace[]>>({});

  const contextValue = useMemo(() => ({
    workspacesLoaded,
    workspaces,
    setWorkspaces,
    workspacesCollection,
    setWorkspacesCollection,
    acceptedWorkspaces,
    pendingWorkspaces,
    workspaceScores,
    setWorkspaceScores,
  }), [
    workspacesLoaded,
    workspaces,
    setWorkspaces,
    workspacesCollection,
    setWorkspacesCollection,
    acceptedWorkspaces,
    pendingWorkspaces,
    workspaceScores,
    setWorkspaceScores,
  ]);

  if (storage.getItem('config.logger-context'))
    console.log("CTX", ["Workspaces", contextValue]);

  return (
    <WorkspacesContext.Provider value={contextValue}>
      {children}
    </WorkspacesContext.Provider>
  );
};

export default WorkspacesProvider;